import { useMutation, useQuery } from '@tanstack/react-query';

import {
  AccessGrantCreatedResponseSchema,
  AccessGrantListResponseSchema,
  CreateAccessGrantRequestSchema,
  type AccessGrantListResponseDto,
  type CreateAccessGrantRequestDto,
} from '@org/contracts';

import { grantStore } from './grant-store';

import { apiClient } from '@/api/client';

// Access grant calls (ADR-0023 §1 and its API reference section).
//
// The issue mutation puts the token straight into the in-memory grant store
// and returns only `{ grantId, expiresAt }`. So the token never sits in the
// TanStack mutation cache, React state or devtools.

export const accessGrantKeys = {
  all: ['access-grants'] as const,
};

/**
 * The server issued a grant, but by the browser clock it has already
 * expired. Usually the computer's clock is wrong. The step-up counts as
 * failed.
 */
export class GrantExpiredOnArrivalError extends Error {
  constructor() {
    super('access grant already expired on arrival');
    this.name = 'GrantExpiredOnArrivalError';
  }
}

export interface IssuedGrantSummary {
  grantId: string;
  expiresAt: string;
}

export function useIssueAccessGrant(tenantId: string) {
  return useMutation<IssuedGrantSummary, unknown, CreateAccessGrantRequestDto>({
    mutationFn: async (body) => {
      const validated = CreateAccessGrantRequestSchema.parse(body);
      const response = await apiClient.post(`/v1/tenants/${tenantId}/access-grants`, validated);
      const created = AccessGrantCreatedResponseSchema.parse(response.data);
      if (!grantStore.set(tenantId, created)) {
        // Unusable here, so end it on the server too. Best effort.
        await endAccessGrant(tenantId, created.grantId).catch(() => {});
        throw new GrantExpiredOnArrivalError();
      }
      return { grantId: created.grantId, expiresAt: created.expiresAt };
    },
    gcTime: 0,
  });
}

export async function endAccessGrant(tenantId: string, grantId: string): Promise<void> {
  await apiClient.delete(`/v1/tenants/${tenantId}/access-grants/${grantId}`);
}

export function useEndAccessGrant() {
  return useMutation<void, unknown, { tenantId: string; grantId: string }>({
    mutationFn: async ({ tenantId, grantId }) => {
      await endAccessGrant(tenantId, grantId);
    },
  });
}

/**
 * The caller's live grants on the server, without tokens. After a reload the
 * SPA has no tokens, but the server still counts those grants. This list
 * lets the admin end them when they hit the limit.
 */
export function useLiveAccessGrants(enabled: boolean) {
  return useQuery<AccessGrantListResponseDto>({
    queryKey: accessGrantKeys.all,
    queryFn: async () => {
      const response = await apiClient.get('/v1/access-grants');
      return AccessGrantListResponseSchema.parse(response.data);
    },
    enabled,
    staleTime: 0,
  });
}
